import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Label, InputText } from '@teo/components';
import { emailRegex } from '@/constants/regex.constants';
import { axios } from '@/axios';
import { config } from '@/config';
import { downloadLink } from '@/util';
import { NotificationContext } from '@/components/Contexts/NotificationContext';
import i18n from 'i18next';

const ReportDownloadModal = ({
  closeModal,
  user,
  payloadDownload,
  courseId,
  lpgroupId,
  organisationId,
}: any) => {
  const { t } = useTranslation();
  const { setShowNotification } = useContext(NotificationContext);

  const [email, setEmail] = useState(user?.email ? user?.email : '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sentReport = async (reportLink: boolean | null) => {
    const recipient = email.trim();
    try {
      setIsSubmitting(true);
      const link = `${config.teoreport}/report?lang=${i18n.language}`;
      const result = await axios.post(link, {
        download: reportLink,
        courseId: courseId,
        lpIds: lpgroupId,
        organisationId: organisationId,
        ...payloadDownload,
        ...(!reportLink ? { email: recipient } : {}),
      });

      if (reportLink && result?.data?.link) {
        downloadLink(result.data.link);
      }

      setShowNotification({
        message: reportLink
          ? t('pages.report_page.report_download_message')
          : t('pages.report.emailed') +
            ` (${result?.data?.email || recipient})`,
        delay: 5000,
      });

      closeModal(false);
    } catch (error: any) {
      console.error('Report request failed:', error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('pages.report.report_error');

      setShowNotification({
        message: errorMessage,
        delay: 5000,
      });

      closeModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      <div className="fixed inset-0 z-[100] flex rounded-xl bg-white/75 px-3">
        <div className="mx-auto my-8 h-max w-full max-w-[600px] rounded-xl bg-white drop-shadow-m">
          <Modal.Header onClose={closeModal}>
            {t('pages.report_page.teams_tab.title_1')}
          </Modal.Header>

          <Modal.Body>
            <h1 className="text-lg font-semibold">
              {t('pages.report_page.teams_tab.download_modal_heading')}
            </h1>
            <p className="mt-2 text-base font-normal text-[#7d7d81]">
              {t('pages.report_page.teams_tab.download_modal_text')}
            </p>

            <form
              className="w-full flex-col max-md:h-full max-md:overflow-hidden"
              data-testid="addTagsForm"
            >
              <div>
                <Label htmlFor="tagName">
                  <p className="mt-6 text-sm font-semibold">
                    {t('pages.report_page.teams_tab.download_modal_label')}
                  </p>
                </Label>
                <InputText
                  id="tagName"
                  type="text"
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  placeholder=""
                  value={email}
                  data-testid="addNameTag"
                />
              </div>
            </form>
          </Modal.Body>

          <Modal.Footer
            cancelButtonLabel={t('pages.manage_tags.cancel_button')}
            onCancel={closeModal}
          >
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                variant="outline"
                type="button"
                disabled={isSubmitting}
                onClick={() => sentReport(true)}
              >
                {t('pages.report_page.teams_tab.btn_download_now')}
              </Button>

              <Button
                type="button"
                onClick={() => sentReport(null)}
                disabled={isSubmitting || !emailRegex.test(email.trim())}
              >
                {t('pages.report_page.teams_tab.btn_send_email')}
              </Button>
            </div>
          </Modal.Footer>
        </div>
      </div>
    </div>
  );
};

export default ReportDownloadModal;
